/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Styles for the question preview page, chapter select, sections and question cards.
 *
 * @file QuestionPreviewStyles.js
 * @author Anuja Gaikwad
 * @since 3.0.0
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const wordWrap = {
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
};

const iconButtonReset = {
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  padding: 0,
  border: 'none',
  backgroundColor: 'transparent',
  color: mixins.color_text_primary,
  cursor: 'pointer',
  borderRadius: 1000,
  '&:focus-visible': {
    outline: `2px solid ${mixins.navyColor}`,
    outlineOffset: 2
  }
};

const questionPreviewStyles = {
  /* Page title, back nav and AI disclaimer are owned by the host (DashboardCard); this is the content panel only. */
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    padding: '24px 16px 16px',
    borderRadius: 24,
    backgroundColor: mixins.lightThemeBgColor2,
    boxSizing: 'border-box',
    '& *': {
      boxSizing: 'border-box'
    }
  },
  chapterSelectWrap: {
    width: '100%',
    paddingBottom: 8,
    [mixins['md-sm-min']]: {
      paddingLeft: 24
    }
  },
  chapterSelectLabel: {
    display: 'block',
    paddingBottom: 4,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.color_text_primary
  },
  /* Questions & Topics modal: full-width field with a visible label, bold value and small counts. */
  sectionSelectWrap: {
    paddingBottom: 16,
    [mixins['md-sm-min']]: {
      paddingLeft: 0
    },
    '& $chapterSelectValue $chapterSelectTitle': {
      fontWeight: mixins.fontWeight600
    },
    '& $chapterSelectCount': {
      fontSize: mixins.fontSize12,
      fontWeight: mixins.fontWeight500,
      lineHeight: '14px',
      color: mixins.zambezi
    },
    // Figma rows are 48px; the theme's 2px focus border on menu items is part of that height.
    '& $chapterMenuItem': {
      padding: '10px 12px'
    },
    '& $chapterMenuItem.Mui-selected $chapterSelectTitle': {
      fontWeight: mixins.fontWeight600
    }
  },
  sectionSelectWrapCompact: {
    paddingBottom: 0
  },
  /* Preview questions page only (variant chapter); not used in Questions & Topics modal. */
  chapterPreviewSelectWrap: {
    '& $chapterMenuPaper': {
      '& .MuiMenuItem-root': {
        whiteSpace: 'normal',
        height: 'auto'
      },
      '& $chapterMenuItem $chapterSelectCount': {
        fontSize: mixins.fontSize12,
        fontWeight: mixins.fontWeight500,
        lineHeight: '14px',
        color: mixins.zambezi
      },
      '& $chapterMenuItem.Mui-disabled $chapterSelectCount': {
        color: mixins.bulkEditCtaButtonDisabledColor
      }
    },
    '& $chapterMenuItem.Mui-selected $chapterSelectTitle': {
      fontWeight: mixins.fontWeight600
    }
  },
  chapterSelect: {
    width: '100%',
    minHeight: 50,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.timeIconColor,
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      minHeight: 50,
      padding: 12,
      paddingRight: 48,
      whiteSpace: 'normal'
    },
    '& .MuiSelect-icon': {
      right: 12,
      width: 24,
      height: 24,
      top: 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      '& svg': {
        width: 10,
        height: 6,
        flexShrink: 0
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2,
      borderWidth: 1
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2,
      borderWidth: 1
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline, &.Mui-expanded .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2,
      borderWidth: 1
    }
  },
  chapterSelectValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: 'calc(100% - 12px)',
    minWidth: 0
  },
  chapterSelectTitle: {
    flex: 1,
    minWidth: 0,
    ...wordWrap
  },
  chapterSelectCount: {
    flexShrink: 0,
    whiteSpace: 'nowrap'
  },
  chapterMenuPaper: {
    maxHeight: 360,
    boxSizing: 'border-box',
    backgroundColor: mixins.chalkWhiteFont,
    boxShadow: 'none',
    overflow: 'hidden',
    paddingRight: 4,
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 4,
      scrollbarColor: `${mixins.amethystLightColor} transparent`,
      borderRadius: 4,
      border: `1px solid ${mixins.amethystColorV2}`,
      overflowY: 'auto'
    }),
    '& .MuiList-root': {
      paddingTop: 4,
      paddingBottom: 4
    },
    '& .MuiMenuItem-root:hover, & .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: mixins.quaternary
    },
    '& .MuiMenuItem-root.Mui-disabled': {
      opacity: 1,
      pointerEvents: 'none',
      backgroundColor: 'transparent',
      color: mixins.bulkEditCtaButtonDisabledColor,
      '&:hover': {
        backgroundColor: 'transparent'
      },
      '& span': {
        color: 'inherit'
      }
    }
  },
  chapterMenuItem: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.timeIconColor,
    whiteSpace: 'normal'
  },
  chapterMenuItemRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0,
    gap: 8
  },
  chapterMenuItemValue: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0
  },
  chapterMenuItemCheckIcon: {
    width: 18,
    height: 18,
    fontSize: 18,
    marginLeft: 8,
    flexShrink: 0,
    display: 'block',
    color: mixins.timeIconColor,
    pointerEvents: 'none'
  },
  chapterMenuItemCheckIconPlaceholder: {
    visibility: 'hidden'
  },
  sectionsScroll: {
    width: '100%',
    maxHeight: 852,
    overflowX: 'hidden',
    // Figma: 1262px sections column + gutter + 4px scrollbar inside the 1280px panel.
    paddingRight: 14,
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 4,
      scrollbarColor: `${mixins.amethystLightColor} transparent`,
      overflowY: 'auto'
    })
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%'
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%'
  },
  sectionHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4,
    minHeight: 40,
    width: '100%',
    [mixins['md-sm-min']]: {
      paddingLeft: 24
    }
  },
  sectionHeaderDisabled: {
    pointerEvents: 'none',
    '& button': {
      cursor: 'default',
      color: mixins.bulkEditCtaButtonDisabledColor
    },
    '& $sectionTitle': {
      color: mixins.bulkEditCtaButtonDisabledColor
    },
    '& $sectionCount': {
      color: mixins.bulkEditCtaButtonDisabledColor
    },
    '& $sectionToggleIcon svg, & $sectionToggleIcon svg path': {
      fill: mixins.bulkEditCtaButtonDisabledColor,
      color: mixins.bulkEditCtaButtonDisabledColor
    }
  },
  sectionToggle: {
    ...iconButtonReset,
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    borderRadius: 12,
    textAlign: 'left',
    minHeight: 40,
    '&:disabled': {
      color: mixins.bulkEditCtaButtonDisabledColor,
      cursor: 'default',
      pointerEvents: 'none'
    }
  },
  sectionToggleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: 18,
    height: 18,
    padding: 0,
    flexShrink: 0,
    '& svg': {
      width: 12,
      height: 7,
      flexShrink: 0
    }
  },
  sectionTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    padding: '0 8px',
    ...wordWrap
  },
  sectionCount: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',
    color: mixins.zambezi,
    whiteSpace: 'nowrap',
    paddingLeft: 12,
    [mixins['md-sm-min']]: {
      paddingRight: 60
    }
  },
  sectionTextDisabled: {
    color: mixins.bulkEditCtaButtonDisabledColor
  },
  questionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    margin: 0,
    padding: 0,
    listStyle: 'none',
    [mixins['md-sm-min']]: {
      paddingLeft: 24
    }
  },
  questionCard: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: 16,
    border: `2px solid ${mixins.color_elevation_alpha_12}`,
    backgroundColor: mixins.chalkWhiteFont
  },
  questionCardInner: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: 16,
    [mixins['md-sm-min']]: {
      padding: 24
    }
  },
  questionCardHeader: {
    ...iconButtonReset,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 8,
    width: '100%',
    borderRadius: 8,
    textAlign: 'left'
  },
  questionCardToggleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: 16,
    height: 16,
    minHeight: 16,
    padding: 0,
    flexShrink: 0,
    '& svg': {
      width: 8,
      height: 5,
      flexShrink: 0
    }
  },
  expandChevronCollapsed: {},
  expandChevronExpanded: {
    transform: 'scaleY(-1)'
  },
  questionCardStem: {
    flex: 1,
    minWidth: 0,
    paddingTop: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px',
    color: mixins.color_text_primary,
    ...wordWrap
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    margin: 0,
    padding: 0,
    listStyle: 'none'
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 48,
    padding: 12,
    borderRadius: 8,
    border: `1px solid ${mixins.color_elevation_alpha_12}`
  },
  optionRowCorrect: {
    backgroundColor: mixins.accentColor,
    borderColor: mixins.accentGreenDark
  },
  optionPrefix: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 24,
    height: 24,
    padding: '0 4px',
    borderRadius: 1000,
    backgroundColor: mixins.quaternary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    color: mixins.color_text_primary
  },
  optionPrefixCorrect: {
    backgroundColor: mixins.prefixCorrectBGColor,
    color: mixins.accentGreen
  },
  optionLabel: {
    flex: 1,
    minWidth: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    color: mixins.color_text_primary,
    ...wordWrap
  },
  optionLabelCorrect: {
    color: mixins.accentGreen
  },
  correctTag: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
    padding: '0 6px 2px',
    flexShrink: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.accentGreen,
    whiteSpace: 'nowrap'
  },
  correctIcon: {
    display: 'inline-flex',
    flexShrink: 0,
    color: mixins.accentGreen,
    '& svg': {
      width: 18,
      height: 18
    }
  },
  emptyMessage: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: mixins.greyScaleColor,
    margin: 0,
    [mixins['md-sm-min']]: {
      paddingLeft: 24
    }
  }
};

export const classes = createScopedClasses('aet-question-preview', questionPreviewStyles);

export const scopedStyles = createScopedStyles(questionPreviewStyles, classes);

export const QuestionPreviewStylesRoot = styled('div')(scopedStyles);

/** Scopes the card on its own so it can render outside the preview tree (e.g. Questions & Topics modal). */
export const QuestionPreviewCardStylesRoot = styled('article')(scopedStyles);

/** Scopes the chapter/section select on its own so it renders correctly inside the Questions & Topics modal. */
export const QuestionPreviewChapterSelectStylesRoot = styled('div')(scopedStyles);

export default questionPreviewStyles;
